import { Box, Typography } from "@mui/material";
import TkTable from "../../../components/atoms/TkTable";
import TkCheckbox from "../../../components/atoms/TkCheckbox";
import TkAmountInput from "../../../components/molecules/TkAmountInput";
import { useEffect, useMemo, useState } from "react";
import TkButton from "../../../components/atoms/TkButton";
import { TkRequiredText } from "../../../components/atoms/TkRequiredText";
import TkHelpIcon from "../../../components/atoms/TkHelpIcon";
import { IAPI_APP007_Response } from "../../../type/apiType";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";

/**
 * 異動した月以前の６か月の本俸月額
 * @param {any} props.data レスポンスデータ
 * @param {any} props.onChange Changeイベント
 * @param {any} props.onButtonClick 「育休確認・登録」ボタン押下時のイベント
 * @param {any} props.checkedStatus 活性非活性の切り替え
 * @return EmploymentStatus
 */
const EmploymentStatus = (props: {
  data: IAPI_APP007_Response;
  onChange?: (arg: any) => void;
  onButtonClick?: () => void;
  checkedStatus: boolean;
}) => {
  const { data, onChange, onButtonClick, checkedStatus } = props;
  const [checkedItems, setCheckedItems] = useState<any>({});

  const headList = [
    {
      headTitle: "氏名",
      className: "staffName",
    },
    {
      top: {
        headTitle: (
          <>
            {data.year}年度　ならない月{" "}
            <TkHelpIcon
              helpMessage={
                <>
                  表示されている年度のならない月を登録してください。
                  <br />
                  ならない月とは、業務に従事した日数（従事したとみなす日を含む）が１０日を超えない月です。
                  <br />
                  詳しくはこちら（別紙PDFへのリンク）をご確認ください。
                  <br />
                  詳しくは
                  <a
                    href={`${
                      process.env.PUBLIC_URL ?? ""
                    }/assets/pdf/≪別紙≫「なる月」「ならない月」について.pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    こちら
                  </a>
                  をご確認ください。
                </>
              }
            />
          </>
        ),
      },
      bottom: [
        { headTitle: "無", keyword: "none" },
        { headTitle: "４月", keyword: "apr" },
        { headTitle: "５月", keyword: "may" },
        { headTitle: "６月", keyword: "jun" },
        { headTitle: "７月", keyword: "jul" },
        { headTitle: "８月", keyword: "aug" },
        { headTitle: "９月", keyword: "sep" },
        { headTitle: "１０月", keyword: "oct" },
        { headTitle: "１１月", keyword: "nov" },
        { headTitle: "１２月", keyword: "dec" },
        { headTitle: "１月", keyword: "jan" },
        { headTitle: "２月", keyword: "feb" },
        { headTitle: "３月", keyword: "mar" },
      ],
    },
    { headTitle: " ", className: "button" },
  ];

  const bodyList: any = [
    [
      { children: data.achievementInfoList?.[0]?.staffName },
      ...headList[1].bottom?.map((i) => {
        if (i.keyword === "none") {
          return {
            children: (
              <TkCheckbox
                checked={Object.values(checkedItems).every(
                  (i) => i === "0" || i === "2"
                )}
                disabled
              />
            ),
            className: "checkbox",
          };
        }

        let checked = checkedItems[i.keyword];
        return {
          children: (
            <TkCheckbox
              onChange={(e: any) => handleCheck(e, i.keyword)}
              disabled={checked === "2" || checked === "3" || checkedStatus}
              checked={checked === "1" || checked === "3"}
            />
          ),
          className: "checkbox",
        };
      })!,
      {
        children: (
          <TkButton onClick={onButtonClick} disabled={checkedStatus}>
            育休確認・登録
          </TkButton>
        ),
        align: "center",
      },
    ],
  ];

  useEffect(() => {
    if (data.achievementInfoList) {
      let newItems = { ...data.achievementInfoList[0] };
      delete newItems.staffName;
      setCheckedItems(newItems);
      onChange && onChange(newItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleCheck = (e: any, k: any) => {
    setCheckedItems({
      ...checkedItems,
      [k]: e.target.checked ? "1" : "0",
    });
  };

  useEffect(() => {
    onChange && onChange(checkedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedItems]);

  return (
    <Box data-testid="employmentStatus">
      <TkTable
        headList={headList}
        bodyList={bodyList}
        headerLeftComponent={<Typography>従業状況</Typography>}
      />
    </Box>
  );
};

/**
 * 異動した月以前の６か月の本俸月額
 * @param {any} props.data レスポンスデータ
 * @param {any} props.onChange Changeイベント
 * @param {any} props.checkedStatus 活性非活性の切り替え
 * @return basicMonthlySalaryOfSix
 */
const BasicMonthlySalaryOfSix = (props: any) => {
  const { data, onChange, checkedStatus = true } = props;
  const [value, setValue] = useState<any>([]);
  const [errorList, setErrorList] = useState<string[]>([]);

  const headList: any = [
    {
      headTitle: "年月",
    },
    {
      headTitle: (
        <Box className="headTitle">
          俸給表の額
          <TkRequiredText />
          <TkHelpIcon
            helpMessage={
              <>
                俸給表の額とは、共済契約者が給与規程で定める本俸月額です。ただし、給与規程で本俸が定められていない職員は、雇用契約書で定める労働条件をもとに計算した額となります。詳しくは、
                <a
                  href={`${
                    process.env.PUBLIC_URL ?? ""
                  }/assets/pdf/≪別紙≫俸給表の額について.pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  こちら
                </a>
                をご確認ください。
                <br />
                【注意点】実際の支給された額を記載するのではなく、給与規程や雇用契約書等に定められた額を記載してください。
              </>
            }
          />
        </Box>
      ),
    },
    {
      headTitle: (
        <Box className="headTitle">
          俸給の調整額
          <TkHelpIcon
            helpMessage={
              <>
                「俸給の調整額」対象手当として当機構が認めた手当があれば、その合計金額を入力してください。
                <br />
                登録されている手当以外の金額は計上することができません。未登録の場合は登録申請が必要です。
                <br />
                ※俸給の調整額については、
                <a
                  href={`${
                    process.env.PUBLIC_URL ?? ""
                  }/assets/pdf/≪別紙≫俸給の調整額について.pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  こちら
                </a>
                をご確認ください。
              </>
            }
          />
        </Box>
      ),
    },
  ];

  const bodyList: any = useMemo(() => {
    return data.map((item: any, index: any) => {
      let yearMonth = item.yearMonth?.toString();

      return [
        {
          children: `${yearMonth.slice(0, 4)}年${yearMonth.slice(4, 6)}月`,
          align: "center",
        },
        {
          children: (
            <TkAmountInput
              wantToSetValue={item.salaryTableAmt}
              inputProps={{ maxLength: 9 }}
              onChange={(e) => {
                handleChange(e, item.yearMonth, index, "salaryTableAmt");
                setErrorList((prevState) => {
                  const res = [...prevState];
                  res[index] = e?.target?.value;
                  return res;
                });
              }}
              error={
                errorList?.[index] === undefined || errorList?.[index] === ""
              }
              errorMessage={
                errorList?.[index] === undefined || errorList?.[index] === ""
                  ? TkPropertyMessageGet("TKMSG_SCR0043E")
                  : ""
              }
              disabled={checkedStatus}
            />
          ),
          align: "left",
        },
        {
          children: (
            <TkAmountInput
              wantToSetValue={item.salaryAdjustmentAmt}
              inputProps={{ maxLength: 9 }}
              onChange={(e) => {
                handleChange(e, item.yearMonth, index, "salaryAdjustmentAmt");
              }}
              disabled={checkedStatus}
            />
          ),
          align: "left",
        },
      ];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorList, data, value]);

  useEffect(() => {
    setValue(data);
    onChange && onChange(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleChange = (event: any, yearMonth: any, index: any, type: any) => {
    setValue((prev: any) => {
      const res = {
        ...prev[index],
        yearMonth: yearMonth,
        [type]: event.target.value,
      };
      return prev.map((v: any, i: any) => (i === index ? res : v));
    });
  };

  useEffect(() => {
    if (errorList.some((error: any) => error === undefined || error === "")) {
      onChange && onChange(undefined);
    } else {
      onChange && onChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Box className="SCRC060115Components" data-testid="basicMonthlySalaryOfSix">
      <TkTable
        headList={headList}
        bodyList={bodyList}
        headerLeftComponent={
          <Typography>異動した月以前の６か月の本俸月額</Typography>
        }
      />
    </Box>
  );
};

export { EmploymentStatus, BasicMonthlySalaryOfSix };
